import {Injectable} from '@angular/core';
import {
  OperationModesEnum
}                   from '../../stores/common/operation-modes.store';
import {
  FertilizerStore
}                   from '../../stores/base-data/fertilizer.store';
import {
  ApTranslationService
}                   from '../../ap-utils/service/ap-translation.service';
import {
  TranslationStore
}                   from '../../stores/translation/translation.store';
import {
  FieldStore
}                   from '../../stores/farm/field.store';
import {
  GetRoundNumericService
}                   from '../../ap-utils/service/get-round-numeric.service';
import {
  ApDateService
}                   from '../../ap-core/services/ap-date-service';
import {
  NFertilizationService
}                   from '../../services/data/n-fertilization.service';
import {
  FertilizerService
}                   from '../../services/data/fertilizer.service';
import {
  FertilizationManagementService
}                   from '../../services/data/fertilization-management.service';
import {
  GetOperationModeService
}                   from '../../ap-utils/service/ap-get-operationmode.service';
import {
  ContentNRelatedPlanBookSourceType,
  IContentNRelatedPlanBook,
  IContentNRelatedPlanBookStatisticValue
}                   from '../../types/n-fertilization.types';
import {
  CropApplicationStore
}                   from '../../stores/task-management/crop-application.store';
import {
  IGridDetailsItemContentRange,
  INFertilizationOverviewGridDetailsItem
}                   from '../types/n-fertilization-overview.types';
import {
  IGridStatus
}                   from '../../ap-interface/interfaces/ap-grid-status.interface';
import {
  ApColumnState
}                   from '../../ap-interface/enums/ap-column-state.enum';
import {
  PlanBooksService
}                   from '../../services/data/plan-books.service';
import {
  SettingsStore
}                   from '../../stores/base-data/settings.store';
import {
  LanguageStore
}                   from '../../stores/translation/language.store';
import INFertilizationPlanBook = Data.TaskManagement.INFertilizationPlanBook;
import IOperationMode = Data.Common.IOperationMode;
import IDatasetFieldMap = Data.Sensor.IDatasetFieldMap;
import IField = Data.FieldManagement.IField;
import IBasicFertilisationPlanBooks = Data.TaskManagement.IBasicFertilisationPlanBooks;
import {ApElementType} from '../../ap-interface/enums/ap-elements-type.enum';
import {FarmCropV2CrStore} from '../../stores/base-data/farm.cropv2cr.store';

@Injectable({providedIn: 'root'})
export class ApNFertilizationCommonService {
  constructor(private languageStore: LanguageStore,
              private fertilizerStore: FertilizerStore,
              private translationService: ApTranslationService,
              private translationStore: TranslationStore,
              private fieldStore: FieldStore,
              private farmCropV2CrStore: FarmCropV2CrStore,
              private cropApplicationStore: CropApplicationStore,
              private fertilizerService: FertilizerService,
              private dateService: ApDateService,
              private operationModeService: GetOperationModeService,
              private nFertilizationService: NFertilizationService,
              private fertilizationManagementService: FertilizationManagementService,
              private roundNumericService: GetRoundNumericService,
              private planBooksService: PlanBooksService,
              private settingsStore: SettingsStore) {
  }

  /**
   * Calculates the average product amount by dividing the given sum by the area.
   * Rounds the result to defined precision if the inputs are valid.
   * @param sum - The total sum to be divided. Must be a finite number or undefined.
   * @param area - The area value by which the sum is divided. Must be greater than zero.
   * @return The calculated average product amount, or undefined if the inputs are invalid.
   * @public
   */
  public calculateProductAvg(sum: number | undefined, area: number): number | undefined {
    return Number.isFinite(sum) && area > 0 ? (sum as number) / area : undefined;
  }

  /**
   * Generates a tooltip string representing a product amount with a specified unit.
   * @param value - The numeric value to be displayed in the tooltip. If undefined or not finite, the method returns undefined.
   * @param unit - The unit of measurement for the numeric value. If undefined, the method returns undefined.
   * @return A formatted string combining the rounded numeric value and the unit, or undefined if inputs are invalid.
   * @public
   */
  public productAmountTooltip(value: number | undefined, unit: string | undefined): string | undefined {
    if (!Number.isFinite(value) || unit === undefined) {
      return undefined;
    }
    const digits = this.settingsStore.FirstSetting?.DigitsAfterDecimalPoint ?? 0;
    const roundedValue = this.roundNumericService.roundAsNumber(value, digits);
    const formattedNumber = this.languageStore.FormatNumber(roundedValue, digits);
    return `${formattedNumber} ${unit}`;
  }

  /**
   * Calculates the application sum based on the provided fertilization plan books.
   * @param planBooks - An array of fertilization plan books used for calculating the application sum.
   * If the array is empty or null, the method returns undefined.
   * @return The calculated application sum. If the operation mode is not valid for summation
   * or if the calculated product amount is not finite, the method returns undefined.
   * @public
   */
  public getApplSum(planBooks: INFertilizationPlanBook[]): number {
    if (!planBooks || planBooks.length === 0) {
      return undefined;
    }
    const planBook = planBooks[0];
    // APV49-1517: For N-Fertilization variants N-Fertilization and Abs. Rapeseed, please do not calculate a sum.
    // In N-Fertilization this is only possible for Target Value and Application Map.
    if (planBook.OperationMode?.Key !== OperationModesEnum.ApplicationMap
      && planBook.OperationMode?.Key !== OperationModesEnum.ApplicationMapAbsolute
      && planBook.OperationMode?.Key !== OperationModesEnum.NfTargetMapPerPlan
      && planBook.OperationMode?.Key !== OperationModesEnum.TargetValue
      && planBook.OperationMode?.Key !== OperationModesEnum.Constant) {
      return undefined;
    }
    const productAmount = this.nFertilizationService.calculateProductAmountFromPlanBooks(planBooks);
    return Number.isFinite(productAmount) ? productAmount : undefined;
  }

  /**
   * Retrieves the display unit for a product in the fertilization plan book.
   * @param planBook - The fertilization plan book containing details about the product and operation mode.
   * @return The translated unit for the product, or an empty string if no unit is applicable
   * based on the operation mode or booking status.
   * @public
   */
  public getProductUnit(planBook: INFertilizationPlanBook): string {
    if (!planBook) {
      return '';
    }
    // APV49-1517: For N-Fertilization variants N-Fertilization and Abs. Rapeseed, please do not calculate a sum.
    // In N-Fertilization this is only possible for Target Value and Application Map.
    // No sum and no unit for special n operation modes.
    // Anyway#1, there will be a sum and a unit if the planBook is booked already!
    // Anyway#2, there will be no sum in case of scan/bonitur
    if (planBook.OperationMode?.Key === OperationModesEnum.Bonitur?.toString() ||
      (!planBook.BookedAt
        && planBook.OperationMode?.Key !== OperationModesEnum.ApplicationMap
        && planBook.OperationMode?.Key !== OperationModesEnum.ApplicationMapAbsolute
        && planBook.OperationMode?.Key !== OperationModesEnum.NfTargetMapPerPlan
        && planBook.OperationMode?.Key !== OperationModesEnum.TargetValue
        && planBook.OperationMode?.Key !== OperationModesEnum.Constant)) {
      return '';
    }
    const product = this.fertilizerStore.getFertilizer(planBook.ProductId);
    let unit = 'Global__Unit_kg'; // fallback
    if (product && product.Unit && product.Unit.BaseUnit) {
      unit = product.Unit.BaseUnit.Key;
    } else if (product && product.Unit) {
      unit = product.Unit.Key;
    }
    return this.translationService.translate(unit);
  }

  /**
   * Retrieves and processes N fertilization applications for a field
   * @param field - Target field
   * @param nFertPlanBooks - N fertilization plan books
   * @param logfiles - Dataset field mappings for N fertilization
   * @param baseFertPlanBooks - Basic fertilization plan books
   * @returns Processed N fertilization applications
   * @public
   */
  public getContentNPlanBooks(field: IField,
                              nFertPlanBooks: INFertilizationPlanBook[],
                              logfiles: IDatasetFieldMap[],
                              baseFertPlanBooks: IBasicFertilisationPlanBooks[]): INFertilizationOverviewGridDetailsItem[] {
    const contentNPlanBooks = this.nFertilizationService.getContentNRelatedPlanBooks(field, nFertPlanBooks, logfiles, baseFertPlanBooks);
    if (!contentNPlanBooks || contentNPlanBooks.length <= 0) {
      return [];
    }
    return contentNPlanBooks.map(contentNPlanBook => {
      const fieldGeom = this.fieldStore.getFieldGeomById(contentNPlanBook.FieldGeomId);
      const farmCrop = this.farmCropV2CrStore.findById(contentNPlanBook.FarmCropId ?? '');
      const operationMode = this.operationModeService.getOperationModeById(contentNPlanBook.OperationModeId);
      const fertilizer = this.fertilizerStore.getFertilizer(contentNPlanBook.ProductId);
      const isBooked = contentNPlanBook?.BookedAt !== null;
      const status = this.getStatusForApplication(contentNPlanBook?.NFertilization, contentNPlanBook?.NUptake,
        contentNPlanBook?.EcValue ?? 0, farmCrop?.Crop?.CropType?.EppoCode, operationMode, contentNPlanBook.SourceType, isBooked);
      const area = fieldGeom?.AreaHa ?? 0;
      const productTotal = contentNPlanBook.NFertilization
        ? this.fertilizationManagementService.calculateProductAmount(contentNPlanBook.ProductId, ApElementType.N, contentNPlanBook.NFertilization.Average, area)
        : undefined;
      const productTotalUnit = this.fertilizerService.getFertilizerUnit(fertilizer, false);
      const productAvg = this.calculateProductAvg(productTotal, area);
      const productAvgUnit = this.fertilizerService.getFertilizerUnit(fertilizer, true);
      const item: INFertilizationOverviewGridDetailsItem = {
        Id: contentNPlanBook.PlanBookId,
        PlanBookId: contentNPlanBook.PlanBookId,
        FieldId: field.Id,
        Status: status.state,
        StatusClass: status.class,
        StatusDesc: status.description,
        ProductName: this._getProductName(operationMode, contentNPlanBook.ProductId),
        ProductId: contentNPlanBook.ProductId,
        CropKey: farmCrop?.Crop?.CropKey,
        WorkArea: this.roundNumericService.roundAsNumber(area, 4),
        Administration: this._getAdministration(contentNPlanBook, farmCrop?.Crop?.CropType?.EppoCode, operationMode),
        Application: this.cropApplicationStore.GetApplication(farmCrop?.Crop?.CropType?.EppoCode, contentNPlanBook.EcValue),
        EC: contentNPlanBook.EcValue,
        Date: contentNPlanBook.ApplicationDate,
        MidnightDate: this.dateService.getDateMidnight(contentNPlanBook.ApplicationDate),
        ProductTotal: productTotal,
        ProductTotalUnit: Number.isFinite(productTotal) && productTotalUnit ? productTotalUnit : '-',
        ProductAvg: Number.isFinite(productAvg) ? productAvg : null,
        ProductAvgUnit: Number.isFinite(productAvg) && productAvgUnit ? productAvgUnit : '-',
        AlreadyApplied: this.nFertilizationService.getAlreadyApplied(contentNPlanBook?.NFertilization?.Average, contentNPlanBook.ProductId),
        FertilizerEfficiency: fertilizer?.Efficiency,
        Absorption: this._getContentRangeValue(contentNPlanBook?.NUptake),
        Fertilization: this._getContentRangeValue(contentNPlanBook?.NFertilization),
        GbiStat: this._getContentRangeValue(contentNPlanBook?.GbiStat)
      };
      return item;
    });
  }

  public getStatusForApplication(nApplication: IContentNRelatedPlanBookStatisticValue | undefined,
                                 nUptake: IContentNRelatedPlanBookStatisticValue | undefined,
                                 ecValue: number, eppoCode: string, operationMode: IOperationMode | null,
                                 sourceType: ContentNRelatedPlanBookSourceType | undefined, isBooked: boolean): IGridStatus {
    let status: IGridStatus;
    if (!nApplication && !nUptake) {
      status = {
        state: ApColumnState.Red,
        class: 'ap-status-red',
        description: this.translationService.translate('Global__Please_Contact_The_Support')
      };
    } else {
      const isApplicationRateNScan = this.cropApplicationStore.GetApplication(eppoCode, ecValue, operationMode) === -1;
      let statusDescription = this.translationService.translate(operationMode.Key);
      if (sourceType && sourceType === ContentNRelatedPlanBookSourceType.BaseFertPlanBook) {
        statusDescription = `${this.translationStore.FindTranslationForSelectedLanguage('Global__BasicFertilization')} / ${statusDescription}`;
      }
      if (isApplicationRateNScan) {
        status = {
          state: ApColumnState.Grey,
          class: 'ap-status-grey',
          description: statusDescription
        };
      } else if (operationMode && operationMode.Key === OperationModesEnum.Constant) {
        status = {
          state: ApColumnState.Yellow,
          class: 'ap-status-yellow',
          description: statusDescription
        };
      } else {
        status = {
          state: ApColumnState.Green,
          class: 'ap-status-green',
          description: statusDescription
        };
      }
    }
    return this.planBooksService.markStatusAsBookedIfApplicable(status, isBooked);
  }

  private _getProductName(operationMode: IOperationMode, productId: number | null): string {
    if (!operationMode || operationMode.Key === OperationModesEnum.Bonitur) {
      return '-';
    }
    if (productId === null || productId === 0) {
      return this.translationStore.FindTranslationForSelectedLanguage('Nutrient__PureNutrient');
    }
    const product = this.fertilizerStore.getFertilizer(productId);
    if (!product) {
      return '-';
    }
    return product.Prodname;
  }

  private _getAdministration(contentNPlanBook: IContentNRelatedPlanBook, eppoCode: string, operationMode: IOperationMode | null): string {
    if (contentNPlanBook.SourceType === ContentNRelatedPlanBookSourceType.BaseFertPlanBook) {
      return 'Global__N0';
    }
    return this.nFertilizationService.getApplicationRateName(eppoCode, contentNPlanBook.EcValue, operationMode, true);
  }

  private _getContentRangeValue(values: IContentNRelatedPlanBookStatisticValue | null): IGridDetailsItemContentRange {
    if (!values) {
      return null;
    }
    return {
      Min: values.Min,
      Max: values.Max,
      Avg: values.Average
    };
  }
}
